// Brain Health (MUI "Standardized" rebuild, route /brain-health). Presentational
// port of the kt BrainHealthPage — same data (useBrainHealth(period): real GET
// /brain/health, sample fallback), rebuilt with mui/* primitives + brainTokens
// under MuiAppShell. The kt original (BrainHealthPage.tsx / BrainHealth.css) is
// untouched; this is its `-mui` twin.
//
// The kt page hand-rolls its charts as inline SVG (Sparkline/AreaChart/Donut/
// HealthGauge/BarChart/GroupedBars) because the stack has no chart library.
// The big "Knowledge growth" area chart reuses the shared mui `AreaChart`
// primitive; the gauge/donut/sparkline/bar shapes it can't express are ported
// here as small inline-SVG components in the same style as mui/AreaChart.tsx —
// no new chart dependency.
import { useState } from "react";
import type { ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import { Box, CircularProgress } from "@mui/material";
import { brainTokens } from "../../design/brainTheme";
import { PageHeader, SectionCard, CardLabel, Segmented, AreaChart, Icon, Sparkle } from "../../mui";
import { useBrainHealth, type BrainHealth, type Period } from "../../lib/use-brain-health";

const c = brainTokens.color;

// Token-resolving chart palette (kt handoff's TEAL/AMBER/RED/GREEN/GREY, mapped
// onto brainTokens — the kt CSS carries two raw greys with "no token
// equivalent"; here they resolve to the closest existing faint/border tokens).
const TEAL = c.tealInk2;
const AMBER = c.warnText2;
const RED = c.dangerText;
const GREEN = c.successText;
const GREY = c.faint; // neutral unknown-state grey
const TRACK = c.borderLight2; // neutral chart track

// ---------------------------------------------------------------------------
// Chart components — ported from the kt page's inline SVG builder functions.
// ---------------------------------------------------------------------------

/** Small inline trend line for a KPI card. */
function Sparkline({ values, color }: { values: number[]; color: string }) {
  const w = 100, h = 30;
  const mx = Math.max(...values), mn = Math.min(...values);
  const d = "M" + values
    .map((v, i) => [i / (values.length - 1) * w, h - 3 - ((v - mn) / ((mx - mn) || 1)) * (h - 6)] as const)
    .map(([x, y]) => `${x.toFixed(1)},${y.toFixed(1)}`)
    .join(" L");
  return (
    <Box component="svg" viewBox={`0 0 ${w} ${h}`} preserveAspectRatio="none" sx={{ width: "100%", height: 30, display: "block" }}>
      <path d={d} fill="none" stroke={color} strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" />
    </Box>
  );
}

/** Stacked-arc donut. */
function Donut({ segs, size }: { segs: { v: number; c: string }[]; size: number }) {
  const r = size / 2 - 9;
  const circ = 2 * Math.PI * r;
  const cx = size / 2;
  const total = segs.reduce((s, x) => s + x.v, 0) || 1;
  let off = 0;
  return (
    <Box component="svg" viewBox={`0 0 ${size} ${size}`} sx={{ width: size, height: size }}>
      <circle cx={cx} cy={cx} r={r} fill="none" stroke={TRACK} strokeWidth={15} />
      {segs.map((s, i) => {
        const len = (s.v / total) * circ;
        const el = (
          <circle
            key={i} cx={cx} cy={cx} r={r} fill="none" stroke={s.c} strokeWidth={15}
            strokeDasharray={`${len} ${circ - len}`} strokeDashoffset={-off}
            transform={`rotate(-90 ${cx} ${cx})`} strokeLinecap="butt"
          />
        );
        off += len;
        return el;
      })}
    </Box>
  );
}

/** 0-1 semicircle gauge arc; arc colour reflects the score band. */
function HealthGauge({ pct, color }: { pct: number; color: string }) {
  const len = Math.PI * 84;
  return (
    <Box component="svg" viewBox="0 0 200 124" sx={{ width: 220, display: "block" }}>
      <path d="M16 116 A84 84 0 0 1 184 116" fill="none" stroke={TRACK} strokeWidth={16} strokeLinecap="round" />
      <path
        d="M16 116 A84 84 0 0 1 184 116" fill="none" stroke={color} strokeWidth={16} strokeLinecap="round"
        strokeDasharray={len} strokeDashoffset={len * (1 - pct)}
      />
    </Box>
  );
}

/** Vertical day bars; the latest `hiFrom..` highlighted teal. */
function BarChart({ values, hiFrom }: { values: number[]; hiFrom: number }) {
  const mx = Math.max(...values) || 1;
  return (
    <Box sx={{ display: "flex", alignItems: "flex-end", gap: "6px", height: 130 }}>
      {values.map((v, i) => (
        <Box
          key={i}
          sx={{
            flex: 1, minWidth: 0, borderRadius: "4px 4px 0 0",
            height: `${(v / mx * 100).toFixed(1)}%`,
            background: i >= hiFrom ? TEAL : c.borderLight3,
          }}
        />
      ))}
    </Box>
  );
}

/** Grouped detected-vs-resolved bars per week. */
function GroupedBars({ pairs }: { pairs: { detected: number; resolved: number }[] }) {
  const mx = pairs.reduce((m, p) => Math.max(m, p.detected, p.resolved), 0) || 1;
  return (
    <Box sx={{ display: "flex", alignItems: "flex-end", gap: "10px", height: 130 }}>
      {pairs.map((p, i) => (
        <Box key={i} sx={{ flex: 1, display: "flex", alignItems: "flex-end", justifyContent: "center", gap: "3px" }}>
          <Box sx={{ width: 9, borderRadius: "3px 3px 0 0", background: RED, height: `${(p.detected / mx * 100).toFixed(1)}%` }} />
          <Box sx={{ width: 9, borderRadius: "3px 3px 0 0", background: TEAL, height: `${(p.resolved / mx * 100).toFixed(1)}%` }} />
        </Box>
      ))}
    </Box>
  );
}

// ---------------------------------------------------------------------------
// Composite widgets.
// ---------------------------------------------------------------------------

const DONUT_PX = 116;

/** Score band -> { label, colour } per the kt page (<50 red / 50-69 amber / 70+ teal). */
function scoreBand(score: number): { label: string; color: string } {
  if (score < 50) return { label: "At risk", color: RED };
  if (score < 70) return { label: "Fair", color: AMBER };
  return { label: "Healthy", color: TEAL };
}

function deltaText(delta: number, unit = ""): { color: string; text: string } {
  if (delta > 0) return { color: GREEN, text: `▲ ${delta}${unit}` };
  if (delta < 0) return { color: RED, text: `▼ ${Math.abs(delta)}${unit}` };
  return { color: c.muted2, text: `${delta}${unit}` };
}

const SOURCE_DOT: Record<"ok" | "lagging" | "off", string> = {
  ok: GREEN, lagging: AMBER, off: GREY,
};

// The kt page's source icons are Tabler names (from live data) that don't
// exist in the mui SVG icon set; map the common ones onto near-equivalents
// and fall back to a generic connector glyph rather than rendering nothing.
const SOURCE_ICON_FALLBACK: Record<string, string> = {
  "brand-slack": "send", mail: "send", ticket: "clipboard-list", "brand-google": "search",
};
function sourceIcon(name: string): string {
  return SOURCE_ICON_FALLBACK[name] ?? "plug";
}

/** Coverage colour by health band (kt handoff: >=90 green, >=85 teal, >=60 amber, else red). */
function coverageColor(pct: number): string {
  if (pct >= 90) return GREEN;
  if (pct >= 85) return TEAL;
  if (pct >= 60) return AMBER;
  return RED;
}

/** A KPI card: label + value + sub-line + sparkline. KpiCard (mui/KpiCard)
 *  doesn't support a trailing sparkline, so this mirrors its styling directly
 *  (CardLabel + big value row) with the extra chart slot the kt page needs. */
function Kpi({ icon, label, value, unit, sub, subColor, spark, sparkColor }: {
  icon: string; label: string; value: string; unit?: string; sub: string; subColor?: string;
  spark: number[]; sparkColor: string;
}) {
  return (
    <SectionCard padding="16px 18px" sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
      <CardLabel icon={<Icon name={icon} size={14} />}>{label}</CardLabel>
      <Box sx={{ display: "flex", alignItems: "baseline", gap: "6px", mt: "10px" }}>
        <Box component="span" sx={{ fontSize: 28, fontWeight: 700, lineHeight: 1, color: c.ink }}>{value}</Box>
        {unit && <Box component="span" sx={{ fontSize: 13, fontWeight: 500, color: c.faint }}>{unit}</Box>}
      </Box>
      <Box sx={{ fontSize: 12, mt: "5px", color: subColor ?? c.muted2 }}>{sub}</Box>
      <Box sx={{ mt: "auto", pt: "12px" }}>
        <Sparkline values={spark} color={sparkColor} />
      </Box>
    </SectionCard>
  );
}

function DonutWidget({ label, segs, centerValue, centerLabel, legend }: {
  label: string; segs: { v: number; c: string }[]; centerValue: string; centerLabel: string;
  legend: { c: string; label: string; v: number }[];
}) {
  return (
    <SectionCard label={label}>
      <Box sx={{ display: "flex", alignItems: "center", gap: "18px" }}>
        <Box sx={{ position: "relative", flexShrink: 0 }}>
          <Donut segs={segs} size={DONUT_PX} />
          <Box sx={{ position: "absolute", inset: 0, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center" }}>
            <Box sx={{ fontSize: 22, fontWeight: 700, color: c.ink, fontVariantNumeric: "tabular-nums" }}>{centerValue}</Box>
            <Box sx={{ fontSize: 10, color: c.faint, textTransform: "uppercase", letterSpacing: "0.1em" }}>{centerLabel}</Box>
          </Box>
        </Box>
        <Box sx={{ display: "flex", flexDirection: "column", gap: "9px", flex: 1, minWidth: 0 }}>
          {legend.map((l) => (
            <Box key={l.label} sx={{ display: "flex", alignItems: "center", gap: "9px", fontSize: 13, color: c.muted2 }}>
              <Box sx={{ width: 10, height: 10, borderRadius: "3px", background: l.c, flexShrink: 0 }} />
              <span>{l.label}</span>
              <Box component="b" sx={{ ml: "auto", color: c.ink, fontVariantNumeric: "tabular-nums" }}>{l.v.toLocaleString()}</Box>
            </Box>
          ))}
        </Box>
      </Box>
    </SectionCard>
  );
}

/** Dense list row shared by Connected sources / Top contributors / Needs a decision. */
function ListRow({ children, first }: { children: ReactNode; first: boolean }) {
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: "12px", py: "11px", borderTop: first ? "none" : `1px solid ${c.borderLight}` }}>
      {children}
    </Box>
  );
}

// ---------------------------------------------------------------------------
// Page.
// ---------------------------------------------------------------------------

const PERIODS: Period[] = ["7d", "30d", "90d"];

export function BrainHealthPageMui() {
  const navigate = useNavigate();
  const [period, setPeriod] = useState<Period>("30d");
  const [scope, setScope] = useState<"company" | "mine">("company");
  const { data, loading } = useBrainHealth(period);

  const page = (body: ReactNode) => (
    <Box sx={{ fontFamily: brainTokens.font.sans, color: c.ink, width: "100%", maxWidth: 1240, p: "24px 32px 56px 0" }}>
      {body}
    </Box>
  );

  if (loading && !data.live) {
    return page(<Box sx={{ display: "flex", justifyContent: "center", py: 8 }}><CircularProgress /></Box>);
  }

  const d: BrainHealth = data;
  const band = scoreBand(d.score);
  const scoreDelta = deltaText(d.scoreDelta);
  const growthDelta = d.series.knowledgeGrowth.length > 1
    ? d.series.knowledgeGrowth[d.series.knowledgeGrowth.length - 1] - d.series.knowledgeGrowth[0]
    : 0;
  const ingestAvg = d.series.ingestionPerDay.length
    ? Math.round(d.series.ingestionPerDay.reduce((a, b) => a + b, 0) / d.series.ingestionPerDay.length)
    : 0;
  const hiFrom = Math.max(0, d.series.ingestionPerDay.length - Math.ceil(d.series.ingestionPerDay.length * 0.14));
  const weatherTotal = d.weather.fresh + d.weather.aging + d.weather.unknown + d.weather.atRisk;
  const freshTotal = d.freshness.under30d + d.freshness.d30to90 + d.freshness.stale;
  const freshPct = freshTotal ? Math.round(d.freshness.under30d / freshTotal * 100) : 0;
  const sourcesHealthy = d.sources.filter((s) => s.status === "ok").length;
  const coverageSub = deltaText(d.coverageDelta, " pts");

  return page(
    <>
      {/* ---- header ---- */}
      <Box sx={{ display: "flex", alignItems: "flex-start", gap: 2, mb: "22px", flexWrap: "wrap" }}>
        <PageHeader title="Brain health" eyebrow="Company-wide knowledge — freshness, growth, and what needs tending" />
        <Box sx={{ ml: "auto", display: "flex", alignItems: "center", gap: "12px", flexWrap: "wrap" }}>
          <Segmented
            value={scope} onChange={setScope}
            options={[{ key: "company", label: "Company-wide" }, { key: "mine", label: "Your brain" }]}
          />
          <Segmented value={period} onChange={setPeriod} options={PERIODS.map((p) => ({ key: p, label: p }))} />
        </Box>
      </Box>

      {/* ---- hero: gauge + KPIs ---- */}
      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "300px 1fr" }, gap: "16px", mb: "16px" }}>
        <SectionCard sx={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center" }}>
          <Box sx={{ display: "flex", alignItems: "center", width: "100%", mb: "14px" }}>
            <CardLabel>Health score</CardLabel>
            <Box sx={{ ml: "auto", fontSize: 12, fontWeight: 500, color: scoreDelta.color }}>{scoreDelta.text} vs last {period}</Box>
          </Box>
          <HealthGauge pct={Math.max(0, Math.min(1, d.score / 100))} color={band.color} />
          <Box sx={{ fontSize: 46, fontWeight: 700, lineHeight: 1, mt: "-42px", color: c.ink, fontVariantNumeric: "tabular-nums" }}>{d.score}</Box>
          <Box sx={{ fontSize: 14, fontWeight: 600, mt: "6px", color: band.color }}>{band.label}</Box>
          <Box sx={{ fontSize: 12, color: c.muted2, mt: "8px", textAlign: "center" }}>
            Composite of freshness, coverage, contradiction load &amp; review lag
          </Box>
        </SectionCard>

        <Box sx={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: "16px" }}>
          <Kpi
            icon="layers" label="Total knowledge"
            value={d.totals.facts.toLocaleString()} unit=" facts"
            sub={`across ${d.totals.pages.toLocaleString()} pages`}
            spark={d.series.sparklines.knowledge} sparkColor={TEAL}
          />
          <Kpi
            icon="chart" label="Coverage"
            value={String(d.coveragePct)} unit="%"
            sub={`${coverageSub.text} · of active areas`} subColor={GREEN}
            spark={d.series.sparklines.coverage} sparkColor={GREEN}
          />
          <Kpi
            icon="help" label="Open questions"
            value={String(d.openQuestions)}
            sub="brain wants resolved"
            spark={d.series.sparklines.questions} sparkColor={AMBER}
          />
          <Kpi
            icon="refresh" label="Contradictions"
            value={String(d.contradictions.open)} unit=" open"
            sub={`${d.contradictions.resolvedAllTime} resolved all-time`}
            spark={d.series.sparklines.contradictions} sparkColor={RED}
          />
        </Box>
      </Box>

      {/* ---- widget grid ---- */}
      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "repeat(2, 1fr)", md: "repeat(3, 1fr)" }, gap: "16px" }}>

        {/* knowledge growth */}
        <SectionCard
          sx={{ gridColumn: { md: "span 2" } }}
          label="Knowledge growth"
          actions={
            <Box sx={{ fontSize: 12, color: c.muted2 }}>
              facts ingested · last {period}{" "}
              <Box component="span" sx={{ color: GREEN, fontWeight: 500 }}>
                {growthDelta >= 0 ? "+" : ""}{growthDelta.toLocaleString()}
              </Box>
            </Box>
          }
        >
          <AreaChart values={d.series.knowledgeGrowth} />
        </SectionCard>

        {/* knowledge weather */}
        <DonutWidget
          label="Knowledge weather"
          segs={[{ v: d.weather.fresh, c: GREEN }, { v: d.weather.aging, c: AMBER }, { v: d.weather.unknown, c: GREY }, { v: d.weather.atRisk, c: RED }]}
          centerValue={weatherTotal.toLocaleString()} centerLabel="items"
          legend={[
            { c: GREEN, label: "Fresh", v: d.weather.fresh },
            { c: AMBER, label: "Aging", v: d.weather.aging },
            { c: GREY, label: "Unknown", v: d.weather.unknown },
            { c: RED, label: "At risk", v: d.weather.atRisk },
          ]}
        />

        {/* ingestion throughput */}
        <SectionCard
          sx={{ gridColumn: { md: "span 2" } }}
          label="Ingestion throughput"
          actions={<Box sx={{ fontSize: 12, color: c.muted2 }}>items / day · avg {ingestAvg}</Box>}
        >
          <BarChart values={d.series.ingestionPerDay} hiFrom={hiFrom} />
          <Box sx={{ display: "flex", justifyContent: "space-between", fontSize: 10, color: c.muted2, mt: "8px", fontFamily: brainTokens.font.mono }}>
            <span>{period} ago</span><span>today</span>
          </Box>
        </SectionCard>

        {/* verification freshness */}
        <DonutWidget
          label="Verification freshness"
          segs={[{ v: d.freshness.under30d, c: GREEN }, { v: d.freshness.d30to90, c: AMBER }, { v: d.freshness.stale, c: GREY }]}
          centerValue={`${freshPct}%`} centerLabel="< 30d"
          legend={[
            { c: GREEN, label: "Verified <30d", v: d.freshness.under30d },
            { c: AMBER, label: "30–90d", v: d.freshness.d30to90 },
            { c: GREY, label: "Stale >90d", v: d.freshness.stale },
          ]}
        />

        {/* coverage by area */}
        <SectionCard
          sx={{ gridColumn: { md: "span 2" } }}
          label="Coverage by area"
          actions={<Box sx={{ fontSize: 12, color: c.muted2 }}>% of topics with current knowledge</Box>}
        >
          <Box sx={{ display: "flex", flexDirection: "column", gap: "11px" }}>
            {d.coverageByArea.map((a) => (
              <Box key={a.area}>
                <Box sx={{ display: "flex", justifyContent: "space-between", fontSize: 13, mb: "5px", color: c.ink }}>
                  <span>{a.area}</span>
                  <Box component="span" sx={{ fontVariantNumeric: "tabular-nums", color: c.muted2, fontWeight: 500 }}>{a.pct}%</Box>
                </Box>
                <Box sx={{ height: 8, background: c.borderLight2, borderRadius: "4px", overflow: "hidden" }}>
                  <Box sx={{ height: "100%", width: `${a.pct}%`, borderRadius: "4px", background: coverageColor(a.pct) }} />
                </Box>
              </Box>
            ))}
          </Box>
        </SectionCard>

        {/* connected sources */}
        <SectionCard
          label="Connected sources"
          actions={<Box sx={{ fontSize: 12, color: c.muted2 }}>{sourcesHealthy} / {d.sources.length} healthy</Box>}
        >
          {d.sources.map((s, i) => (
            <ListRow key={s.name} first={i === 0}>
              <Box sx={{ width: 30, height: 30, borderRadius: "8px", background: c.borderLight3, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, color: c.muted2 }}>
                <Icon name={sourceIcon(s.icon)} size={16} />
              </Box>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Box sx={{ fontSize: 14, color: c.ink, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{s.name}</Box>
                <Box sx={{ fontSize: 12, color: c.muted2, mt: "2px" }}>{s.lastSync}</Box>
              </Box>
              <Box sx={{ width: 9, height: 9, borderRadius: "50%", background: SOURCE_DOT[s.status], flexShrink: 0 }} />
            </ListRow>
          ))}
        </SectionCard>

        {/* contradictions over time */}
        <SectionCard
          sx={{ gridColumn: { md: "span 2" } }}
          label="Contradictions · detected vs resolved"
          actions={<Box sx={{ fontSize: 12, color: c.muted2 }}>last 8 weeks</Box>}
        >
          <GroupedBars pairs={d.series.contradictions} />
          <Box sx={{ display: "flex", gap: "16px", mt: "12px", fontSize: 12, color: c.muted2 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: "6px" }}><Box sx={{ width: 10, height: 10, borderRadius: "3px", background: RED }} />Detected</Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: "6px" }}><Box sx={{ width: 10, height: 10, borderRadius: "3px", background: TEAL }} />Resolved</Box>
          </Box>
        </SectionCard>

        {/* top contributors */}
        <SectionCard label="Top contributors" actions={<Box sx={{ fontSize: 12, color: c.muted2 }}>30d</Box>}>
          {d.contributors.map((cc, i) => (
            <ListRow key={cc.name} first={i === 0}>
              <Box sx={{ width: 26, height: 26, borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 10, fontWeight: 600, flexShrink: 0, background: cc.bg, color: cc.fg }}>
                {cc.isAgent ? <Sparkle size={13} /> : cc.initials}
              </Box>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Box sx={{ fontSize: 14, color: c.ink, display: "flex", alignItems: "center", gap: "6px" }}>
                  {cc.name}
                  {cc.isAgent && (
                    <Box component="span" sx={{ fontSize: 10, color: c.tealInk, background: c.tealTint, borderRadius: "4px", padding: "2px 6px" }}>agent</Box>
                  )}
                </Box>
                {cc.isAgent && <Box sx={{ fontSize: 12, color: c.muted2, mt: "2px" }}>synthesised &amp; merged</Box>}
              </Box>
              <Box sx={{ fontSize: 13, fontWeight: 600, color: c.ink, fontVariantNumeric: "tabular-nums" }}>{cc.count.toLocaleString()}</Box>
            </ListRow>
          ))}
        </SectionCard>

        {/* needs a decision */}
        <SectionCard
          sx={{ gridColumn: { md: "span 2" } }}
          label="Needs a decision"
          actions={
            <Box sx={{ display: "flex", alignItems: "center", gap: "14px" }}>
              <Box sx={{ fontSize: 12, color: c.muted2 }}>open contradictions</Box>
              <Box
                component="button"
                onClick={() => navigate("/standup")}
                sx={{ border: "none", background: "none", color: c.teal, fontWeight: 500, fontSize: 12, cursor: "pointer", p: 0, fontFamily: brainTokens.font.sans }}
              >
                Open standup →
              </Box>
            </Box>
          }
        >
          {d.openContradictions.length === 0 ? (
            <Box sx={{ fontSize: 13, color: c.muted2, py: "11px" }}>No open contradictions — the brain is settled.</Box>
          ) : d.openContradictions.map((cc, i) => (
            <ListRow key={i} first={i === 0}>
              <Box sx={{ width: 9, height: 9, borderRadius: "50%", background: cc.severity === "red" ? RED : AMBER, flexShrink: 0 }} />
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Box sx={{ fontSize: 14, color: c.ink, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{cc.title}</Box>
                <Box sx={{ fontSize: 12, color: c.muted2, mt: "2px" }}>{cc.detail}</Box>
              </Box>
              <Box sx={{ fontSize: 12, color: c.muted2, fontFamily: brainTokens.font.mono, flexShrink: 0 }}>{cc.ageDays}d open</Box>
            </ListRow>
          ))}
        </SectionCard>

      </Box>
    </>,
  );
}
